import { ReactNode, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { DocumentRenderData } from '../types'

export const OPEN_1031_ADDRESS_LINES = [
  'Open 1031, Inc., a Delaware corporation',
  '900 E. Hamilton Avenue, Suite 100',
  'Campbell, California 95008',
]

export const DEFAULT_INTERMEDIARY_OFFICER = 'Tom Bottenberg'
export const DEFAULT_INTERMEDIARY_TITLE = 'Chief Operations Officer'
export const QI_COUNTER_SIGNATORY_NAME = 'Tom Bottenberg'
export const QI_COUNTER_SIGNATORY_TITLE = 'Authorized Signatory'

export function blank(value: string | undefined, width: number = 40): string {
  if (value && value.trim()) return value
  return '_'.repeat(width)
}

export function formatUSD(value: number | string | undefined): string {
  if (value == null) return ''
  const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ''))
  if (!isFinite(numeric) || numeric === 0) return ''
  return numeric.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

export function formatDate(value: string | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (isNaN(d.getTime())) return value
  return d.toLocaleDateString()
}

interface MergeFieldProps {
  label: string
  value: string | undefined
  width?: number
  prefix?: string
}

export function MergeField({ label, value, width = 40, prefix }: MergeFieldProps) {
  return (
    <p className="text-xs">
      <span className="font-semibold">{label}</span>{' '}
      {prefix && <span>{prefix}</span>}
      <span className="font-mono">{blank(value, width)}</span>
    </p>
  )
}

interface CollapsibleClauseProps {
  title: string
  children: ReactNode
  defaultOpen?: boolean
}

export function CollapsibleClause({ title, children, defaultOpen = false }: CollapsibleClauseProps) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-border rounded-md bg-card/40">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        )}
      </button>
      {open && <div className="px-3 pb-3 text-xs leading-relaxed space-y-2">{children}</div>}
    </div>
  )
}

interface SignatureBlockProps {
  label: string
  signerName?: string
  signature: DocumentRenderData['signature']
  borderColorClass?: string
}

export function SignatureBlock({
  label,
  signerName,
  signature,
  borderColorClass = 'border-primary',
}: SignatureBlockProps) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground mb-2">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs items-end">
        <div className="md:col-span-2">
          {signature?.captured ? (
            <div className={`p-3 bg-background border-2 ${borderColorClass} rounded min-h-[52px] flex items-center`}>
              {signature.type === 'type' ? (
                <span
                  className="text-xl text-primary-900 dark:text-primary-100"
                  style={{ fontFamily: 'cursive' }}
                >
                  {signature.value}
                </span>
              ) : (
                signature.value && (
                  <img
                    src={signature.value}
                    alt="Signature"
                    className="max-h-[44px] object-contain"
                  />
                )
              )}
            </div>
          ) : (
            <div className="border-b-2 border-foreground/30 pb-1 h-8 italic text-muted-foreground/80">
              Signature will be captured below
            </div>
          )}
          {signerName && (
            <p className="mt-1 text-[11px] text-muted-foreground">{signerName}</p>
          )}
        </div>
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground mb-0.5">Dated:</p>
          <p className="font-mono">
            {signature?.capturedAt
              ? new Date(signature.capturedAt).toLocaleDateString()
              : new Date().toLocaleDateString()}
          </p>
        </div>
      </div>
    </div>
  )
}

interface QISignatureBlockProps {
  signature: DocumentRenderData['signature']
  signatoryName?: string
  signatoryTitle?: string
  borderColorClass?: string
}

export function QISignatureBlock({
  signature,
  signatoryName = QI_COUNTER_SIGNATORY_NAME,
  signatoryTitle = QI_COUNTER_SIGNATORY_TITLE,
  borderColorClass = 'border-emerald-600',
}: QISignatureBlockProps) {
  if (!signature?.captured) return null
  const signingDate = signature.capturedAt
    ? new Date(signature.capturedAt).toLocaleDateString()
    : new Date().toLocaleDateString()
  return (
    <div aria-label="QI counter-signature block">
      <p className="text-xs font-semibold text-muted-foreground mb-2">OPEN 1031, INC. (QUALIFIED INTERMEDIARY):</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs items-end">
        <div className="md:col-span-2">
          <div className={`p-3 bg-background border-2 ${borderColorClass} rounded min-h-[52px] flex items-center`}>
            <span
              className="text-xl text-emerald-900 dark:text-emerald-100"
              style={{ fontFamily: 'cursive' }}
            >
              {signatoryName}
            </span>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {signatoryName}, {signatoryTitle}, Open 1031, Inc.
          </p>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-muted-foreground mb-0.5">Dated:</p>
          <p className="font-mono">{signingDate}</p>
        </div>
      </div>
    </div>
  )
}

interface IntermediaryBlockProps {
  data: DocumentRenderData
  signerNameOverride?: string
  signerTitleOverride?: string
}

export function IntermediaryBlock({ data, signerNameOverride, signerTitleOverride }: IntermediaryBlockProps) {
  const meta = data.bundleMeta
  const officerName = signerNameOverride ?? meta?.intermediaryOfficerName ?? DEFAULT_INTERMEDIARY_OFFICER
  const officerTitle = signerTitleOverride ?? meta?.intermediaryOfficerTitle ?? DEFAULT_INTERMEDIARY_TITLE
  return (
    <div className="space-y-1 text-xs">
      <p className="font-semibold uppercase">Intermediary:</p>
      {OPEN_1031_ADDRESS_LINES.map((line) => (
        <p key={line}>{line}</p>
      ))}
      <p className="pt-2">By: <span className="font-mono">{blank(undefined, 30)}</span></p>
      <p>Name: <span className="font-mono">{officerName}</span></p>
      <p>Title: <span className="font-mono">{officerTitle}</span></p>
      <p>Date: <span className="font-mono">
        {data.signature?.capturedAt
          ? new Date(data.signature.capturedAt).toLocaleDateString()
          : new Date().toLocaleDateString()}
      </span></p>
    </div>
  )
}
